import {
	type Actor,
	type BusinessId,
	type BusinessRole,
	can,
	createActor,
	type Permission,
	type PlatformRole,
	type UserId,
} from "@nwga/auth/permissions";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useTRPC } from "./trpc.tsx";

/**
 * The same authorization rules the server enforces, in the browser.
 *
 * `@nwga/auth/permissions` imports nothing but `@nwga/contracts`, which is what
 * makes this possible: the client decides whether to render a button by asking
 * the *identical* function the service calls before doing the work. A button
 * that renders and then fails is a support email; a rule copied into the UI by
 * hand is one that drifts.
 *
 * The session query already carries what an `Actor` needs — the platform role
 * and the membership map — so none of this costs a request. It stays an
 * affordance and never a gate: every procedure authorises independently, and
 * anything hidden here is refused there too.
 */

interface SessionMembership {
	readonly businessId: string;
	readonly role: string;
}

/**
 * The signed-in caller, with memberships resolved — `null` while the session is
 * still loading and for anonymous visitors.
 *
 * Memoised on the session data rather than rebuilt per render, so the returned
 * actor is stable enough to sit in a dependency array.
 */
export function useActor(): Actor | null {
	const trpc = useTRPC();
	const session = useQuery(trpc.session.queryOptions());

	const user = session.data?.user ?? null;
	const memberships: readonly SessionMembership[] =
		session.data?.memberships ?? [];

	return React.useMemo(() => {
		if (user === null) return null;

		return createActor(
			user.id as UserId,
			user.role as PlatformRole,
			memberships.map(
				(membership) =>
					[
						membership.businessId as BusinessId,
						membership.role as BusinessRole,
					] as const,
			),
		);
	}, [user, memberships]);
}

/**
 * Whether the viewer may do something, optionally within one business.
 *
 * ```tsx
 * const canInvite = useCan("business.member.manage", business.id);
 * ```
 *
 * Omitting `businessId` asks the platform-wide question, which is the right one
 * for top-level navigation: "may this person moderate anything at all?"
 */
export function useCan(permission: Permission, businessId?: string): boolean {
	const actor = useActor();
	if (actor === null) return false;

	return can(actor, permission, {
		...(businessId === undefined
			? {}
			: { businessId: businessId as BusinessId }),
	});
}

/** The viewer's role inside a business, or `undefined` if they are not a member. */
export function useBusinessRole(
	businessId: string | undefined,
): BusinessRole | undefined {
	const actor = useActor();
	if (actor === null || businessId === undefined) return undefined;

	return actor.memberships.get(businessId as BusinessId);
}
